import { ProductsCatalog } from '../../../pages/ProductsPage';
import { OptImg } from '../../ui/OptImg';
import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { CmsButton, SmartLink, SectionShell, SectionHeader, isDarkBg, inPreviewFrame } from '../shared';

const plain = (s) => (typeof s === 'string' ? s.replace(/<[^>]+>/g, '') : s);

const Card = ({ item, i, dark, showPrice, subtitleKey = 'subtitle' }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.08 }}>
    <SmartLink to={item.url} className="group block" data-testid={`catalog-card-${item.id}`}>
      <div className="overflow-hidden bg-neutral-100 rounded-[var(--site-radius)] aspect-[4/3]">
        {item.image ? <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={item.image} alt={item.name || item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          : <div className="w-full h-full flex items-center justify-center text-neutral-400 text-sm">No image</div>}
      </div>
      <div className="mt-5">
        <h3 className={`!text-xl !font-semibold ${dark ? 'text-white' : 'text-neutral-900'} group-hover:text-[color:var(--site-accent)] transition-colors`}>{item.display_name || item.name || item.title}</h3>
        {(item[subtitleKey] || item.short_description || item.description || item.excerpt) && <p className={`mt-2 text-sm line-clamp-2 ${dark ? 'text-neutral-300' : 'text-neutral-600'}`}>{plain(item[subtitleKey] || item.short_description || item.description || item.excerpt)}</p>}
        {item.base_price ? <p className="mt-2 text-sm text-neutral-500">From ${item.base_price}{item.on_sale && item.sale_label ? <span className="ml-2 text-[10px] uppercase tracking-wider bg-[color:var(--site-accent)] text-white px-1.5 py-0.5">{item.sale_label}</span> : null}</p> : null}
        <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[color:var(--site-accent)]">Learn more <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
      </div>
    </SmartLink>
  </motion.div>
);

const Empty = ({ what }) => <p className="text-sm text-neutral-400 italic">No {what} selected yet — choose items in the section settings.</p>;

export const ProductGridSection = ({ section }) => {
  if (section.layout === 'interactive') {
    const p = section.props || {};
    return (
      <SectionShell section={{ ...section, style: { background: 'light', ...(section.style || {}), padding: 'none' } }} container="none" testId="section-product_grid">
        <div className="min-h-screen pt-24 pb-16 cms-native"><ProductsCatalog embedded navigateOnTab={!inPreviewFrame()} header={!!(p.title || p.eyebrow)} eyebrow={p.eyebrow} title={p.title} body={p.body} initialCategory={p.initial_category} /></div>
      </SectionShell>
    );
  }
  const items = section.data || [];
  const p = section.props || {};
  const dark = isDarkBg(section.style);
  const layout = section.layout || 'cards';
  return (
    <SectionShell section={section}>
      <SectionHeader props={p} style={section.style} dark={dark} />
      {items.length === 0 && <Empty what="products" />}
      {layout === 'list' ? (
        <div className="divide-y divide-neutral-200">
          {items.map((it) => (
            <SmartLink key={it.id} to={it.url} className="flex flex-col sm:flex-row gap-6 py-8 group">
              {it.image && <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={it.image} alt={it.title} className="w-full sm:w-56 aspect-[4/3] object-cover rounded-[var(--site-radius)]" loading="lazy" />}
              <div className="flex-1">
                <h3 className={`!text-xl !font-semibold ${dark ? 'text-white' : 'text-neutral-900'} group-hover:text-[color:var(--site-accent)]`}>{it.display_name || it.title}</h3>
                <p className={`mt-2 ${dark ? 'text-neutral-300' : 'text-neutral-600'}`}>{it.short_description || it.subtitle}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[color:var(--site-accent)]">View details <ArrowRight className="w-4 h-4" /></span>
              </div>
            </SmartLink>
          ))}
        </div>
      ) : layout === 'featured' && items.length > 0 ? (
        <div className="grid lg:grid-cols-2 gap-8">
          <SmartLink to={items[0].url} className="group block">
            <div className="overflow-hidden rounded-[var(--site-radius)] aspect-[4/5] lg:aspect-auto lg:h-full bg-neutral-100">{items[0].image && <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={items[0].image} alt={items[0].title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
            <h3 className={`mt-5 !text-2xl !font-semibold ${dark ? 'text-white' : 'text-neutral-900'}`}>{items[0].display_name || items[0].title}</h3>
          </SmartLink>
          <div className="grid sm:grid-cols-2 gap-8">{items.slice(1, 5).map((it, i) => <Card key={it.id} item={it} i={i} dark={dark} />)}</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">{items.map((it, i) => <Card key={it.id} item={it} i={i} dark={dark} />)}</div>
      )}
      {p.button?.text && <div className="mt-12 text-center"><CmsButton button={p.button} variant={dark ? 'light' : 'secondary'} /></div>}
    </SectionShell>
  );
};

export const CategoryGridSection = ({ section }) => {
  const items = section.data || [];
  const dark = isDarkBg(section.style);
  return (
    <SectionShell section={section}>
      <SectionHeader props={section.props} style={section.style} dark={dark} />
      {items.length === 0 && <Empty what="categories" />}
      {section.layout === 'tiles' ? (
        <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
          {items.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <SmartLink to={c.url} className="group relative block aspect-[3/4] overflow-hidden rounded-[var(--site-radius)] bg-neutral-800" data-testid={`category-tile-${c.slug}`}>
                {c.image && <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />}
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/90 via-neutral-900/20 to-transparent" />
                <div className="absolute bottom-0 p-8 text-white">
                  <h3 className="!text-2xl !font-semibold">{c.name}</h3>
                  {c.description && <p className="mt-2 text-sm text-neutral-300 line-clamp-2">{plain(c.description)}</p>}
                  <span className="mt-4 inline-flex items-center gap-2 text-sm uppercase tracking-wider">Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </SmartLink>
            </motion.div>
          ))}
        </div>
      ) : (
        // Mirrors the coded HomePage "Window Treatment Solutions" cards (.card-product).
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <SmartLink to={c.url} className="card-product block group" data-testid={`catalog-card-${c.id}`}>
                <div className="aspect-[4/3] overflow-hidden">{c.image && <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />}</div>
                <div className="p-6">
                  <h3 className="!text-xl !font-semibold text-neutral-900 mb-2">{c.display_name || c.name}</h3>
                  {(c.description || c.short_description) && <p className="text-neutral-600 text-sm mb-4 line-clamp-2">{plain(c.description || c.short_description)}</p>}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-500">{c.base_price ? `From $${c.base_price}` : ''}</span>
                    <span className="text-sm font-medium text-neutral-900 flex items-center gap-1 group-hover:gap-2 transition-all">Learn More <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </div>
              </SmartLink>
            </motion.div>
          ))}
        </div>
      )}
    </SectionShell>
  );
};

const Swatches = ({ swatches = [], size = 'w-8 h-8' }) => (
  <div className="flex flex-wrap gap-1.5">
    {swatches.map((s) => <span key={s.id} title={s.name} className={`${size} rounded-full border border-black/10 bg-cover bg-center`} style={{ backgroundColor: s.hex_color || '#ddd', backgroundImage: s.image_url ? `url(${s.image_url})` : undefined }} />)}
  </div>
);

export const CollectionGridSection = ({ section }) => {
  const items = section.data || [];
  const p = section.props || {};
  const dark = isDarkBg(section.style);
  return (
    <SectionShell section={section}>
      <SectionHeader props={p} style={section.style} dark={dark} />
      {items.length === 0 && <Empty what="collections" />}
      {section.layout === 'swatch_rows' ? (
        <div className="divide-y divide-neutral-200">
          {items.map((c) => (
            <SmartLink key={c.id} to={c.url} className="flex flex-col md:flex-row md:items-center gap-4 md:gap-10 py-6 group" data-testid={`collection-row-${c.id}`}>
              <div className="md:w-72">
                <h3 className={`!text-lg !font-semibold ${dark ? 'text-white' : 'text-neutral-900'} group-hover:text-[color:var(--site-accent)]`}>{c.name}</h3>
                <p className="text-xs text-neutral-500 uppercase tracking-wider">{[c.fabric_type, c.openness].filter(Boolean).join(' · ')}{c.color_count ? ` · ${c.color_count} colors` : ''}</p>
              </div>
              <div className="flex-1"><Swatches swatches={c.swatches} size="w-10 h-10" /></div>
              <ArrowRight className="w-5 h-5 text-neutral-400 group-hover:text-[color:var(--site-accent)] hidden md:block" />
            </SmartLink>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}>
              <SmartLink to={c.url} className="group block border border-neutral-200 p-6 rounded-[var(--site-radius)] hover:border-neutral-400 transition-colors bg-white" data-testid={`collection-card-${c.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-neutral-100 rounded-[var(--site-radius)]">{c.image && <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}</div>
                <h3 className="mt-5 !text-xl !font-semibold text-neutral-900">{c.name}</h3>
                <p className="text-xs text-neutral-500 uppercase tracking-wider mt-1">{[c.fabric_type, c.openness].filter(Boolean).join(' · ')}{c.color_count ? ` · ${c.color_count} colors` : ''}</p>
                <div className="mt-4"><Swatches swatches={c.swatches} /></div>
              </SmartLink>
            </motion.div>
          ))}
        </div>
      )}
      {p.button?.text && <div className="mt-12 text-center"><CmsButton button={p.button} variant={dark ? 'light' : 'secondary'} /></div>}
    </SectionShell>
  );
};

export const FabricGridSection = ({ section }) => {
  const items = section.data || [];
  const dark = isDarkBg(section.style);
  return (
    <SectionShell section={section}>
      <SectionHeader props={section.props} style={section.style} dark={dark} />
      {items.length === 0 && <Empty what="fabrics" />}
      <div className={`grid gap-4 ${section.layout === 'cards' ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-3 sm:grid-cols-5 lg:grid-cols-8'}`}>
        {items.map((f) => (
          <SmartLink key={f.id} to={f.url} className="group block" title={f.name} data-testid={`fabric-swatch-${f.id}`}>
            <div className={`${section.layout === 'cards' ? 'aspect-[4/3]' : 'aspect-square'} rounded-[var(--site-radius)] border border-black/10 bg-cover bg-center transition-transform group-hover:scale-[1.03]`} style={{ backgroundColor: f.hex_color || '#ddd', backgroundImage: f.image ? `url(${f.image})` : undefined }} />
            <p className={`mt-2 text-xs ${section.layout === 'cards' ? 'text-sm font-medium' : ''} ${dark ? 'text-neutral-200' : 'text-neutral-800'} truncate`}>{f.name}</p>
            {section.layout === 'cards' && <p className="text-xs text-neutral-500 truncate">{f.collection_name}</p>}
          </SmartLink>
        ))}
      </div>
    </SectionShell>
  );
};

export const ProjectsSection = ({ section }) => {
  const items = section.data || [];
  const p = section.props || {};
  const dark = isDarkBg(section.style);
  return (
    <SectionShell section={section}>
      <SectionHeader props={p} style={section.style} dark={dark} />
      {items.length === 0 && <Empty what="projects" />}
      {section.layout === 'showcase' ? (
        <div className="space-y-20">
          {items.map((pr, i) => (
            <div key={pr.id} className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
              {pr.image && <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={pr.image} alt={pr.title} className="w-full aspect-[4/3] object-cover rounded-[var(--site-radius)]" loading="lazy" />}
              <div>
                <span className="label-overline text-[color:var(--site-accent)]">{[pr.category, pr.location].filter(Boolean).join(' · ')}</span>
                <h3 className={`mt-3 !text-2xl !font-semibold ${dark ? 'text-white' : 'text-neutral-900'}`}>{pr.title}</h3>
                <p className={`mt-4 ${dark ? 'text-neutral-300' : 'text-neutral-600'}`}>{pr.description}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">{items.map((pr, i) => <Card key={pr.id} item={{ ...pr, url: pr.url || '/gallery' }} i={i} dark={dark} subtitleKey="description" />)}</div>
      )}
      {p.button?.text && <div className="mt-12 text-center"><CmsButton button={p.button} variant={dark ? 'light' : 'secondary'} /></div>}
    </SectionShell>
  );
};

export const BlogGridSection = ({ section }) => {
  const items = section.data || [];
  const p = section.props || {};
  const dark = isDarkBg(section.style);
  return (
    <SectionShell section={section}>
      <SectionHeader props={p} style={section.style} dark={dark} />
      {items.length === 0 && <Empty what="articles" />}
      <div className={section.layout === 'list' ? 'divide-y divide-neutral-200' : 'grid sm:grid-cols-2 lg:grid-cols-3 gap-8'}>
        {items.map((a, i) => section.layout === 'list' ? (
          <SmartLink key={a.id} to={a.url} className="block py-6 group">
            <span className="label-overline text-[color:var(--site-accent)]">{a.category}</span>
            <h3 className={`mt-1 !text-xl !font-semibold ${dark ? 'text-white' : 'text-neutral-900'} group-hover:text-[color:var(--site-accent)]`}>{a.title}</h3>
            <p className={`mt-2 text-sm ${dark ? 'text-neutral-300' : 'text-neutral-600'}`}>{a.excerpt}</p>
          </SmartLink>
        ) : <Card key={a.id} item={a} i={i} dark={dark} subtitleKey="excerpt" />)}
      </div>
      {p.button?.text && <div className="mt-12 text-center"><CmsButton button={p.button} variant={dark ? 'light' : 'secondary'} /></div>}
    </SectionShell>
  );
};

export const RelatedSection = ({ section }) => {
  const items = section.data || [];
  const dark = isDarkBg(section.style);
  return (
    <SectionShell section={{ ...section, style: { padding: 'compact', ...(section.style || {}) } }}>
      <SectionHeader props={section.props} style={section.style} dark={dark} className="mb-8" />
      {items.length === 0 && <Empty what="related items" />}
      {section.layout === 'compact' ? (
        <div className="flex flex-wrap gap-3">{items.map((it) => <SmartLink key={it.id} to={it.url} className="px-4 py-2 border border-neutral-300 text-sm hover:border-neutral-900 rounded-[var(--site-btn-radius)]">{it.display_name || it.name || it.title}</SmartLink>)}</div>
      ) : <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{items.map((it, i) => <Card key={it.id} item={it} i={i} dark={dark} />)}</div>}
    </SectionShell>
  );
};
